import { ActionTree, MutationTree } from "vuex";
import axios, { AxiosResponse } from "axios";
import { BlacklistPayload } from "@/models/blacklist/BlacklistPayload";
import { BlacklistReasons } from "@/models/blacklist/BlacklistReasons";
import { BlacklistState } from "@/models/store/BlacklistState";
import { BlacklistTenants } from "@/models/blacklist/BlacklistTenants";
import { RootState } from "@/models/store/RootState";

export const formatTenant = (tenants: BlacklistTenants[]) => {
  return tenants.map(tenant => {
    const reason = (typeof tenant.reason === "string") ? tenant.reason : tenant.reason!.alias;
    return {
      id: tenant.id,
      customer_name: tenant.customer_name,
      drivers_license_number: tenant.drivers_license_number,
      email_address: tenant.email_address,
      location_code: tenant.facility!.location_code,
      note: tenant.note,
      phone_number: tenant.phone_number,
      reason: reason as string,
      created_at: tenant.created_at,
      updated_at: tenant.updated_at
    };
  });
};

export const state = {
  blacklistReasons: [],
  blacklistTenants: [],
  loadingState: []
};

export const actions: ActionTree<BlacklistState, RootState> = {
  getBlacklistReasons: ({ commit, dispatch }) => {
    commit("addToLoadingState", "getting all blacklist reasons");
    return axios.get(`/blacklist-reasons`)
      .then((resp: AxiosResponse) => {
        commit("setBlacklistReasons", resp.data);
        commit("removeFromLoadingState", "getting all blacklist reasons");
      }).catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting all blacklist reasons");
        throw new Error(err);
      });
  },
  getBlacklistTenants: ({ commit, dispatch }) => {
    commit("addToLoadingState", "getting all blacklist tenants");
    return axios.get(`/blacklist-customers`)
      .then((resp: AxiosResponse) => {
        commit("setBlacklistTenants", formatTenant(resp.data));
        commit("removeFromLoadingState", "getting all blacklist tenants");
      }).catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting all blacklist tenants");
        throw new Error(err);
      });
  },
  createBlacklistTenant: ({ commit, dispatch }, blacklistPayload: BlacklistPayload) => {
    commit("addToLoadingState", "creating blacklist tenant");
    return axios.post(`/blacklist-customers`, blacklistPayload)
      .then(() => {
        dispatch("getBlacklistTenants");
        dispatch(
          "snackbar/successMessage",
          `Blocked tenant successfully created.`,
          { root: true }
        );
        commit("removeFromLoadingState", "creating blacklist tenant");
      }).catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "creating blacklist tenant");
        throw new Error(err);
      });
  },
  updateBlacklistTenant: ({ commit, dispatch }, blacklistPayload: BlacklistPayload) => {
    commit("addToLoadingState", "updating blacklist tenant");
    return axios.patch(`/blacklist-customers/${blacklistPayload.id}`, blacklistPayload)
      .then(() => {
        dispatch("getBlacklistTenants");
        dispatch(
          "snackbar/successMessage",
          `Blocked tenant successfully updated.`,
          { root: true }
        );
        commit("removeFromLoadingState", "updating blacklist tenant");
      }).catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "updating blacklist tenant");
        throw new Error(err);
      });
  },
  deleteBlacklistTenant: ({ commit, dispatch }, blacklistTenantId: number) => {
    commit("addToLoadingState", "deleting blacklist tenant");
    return axios.delete(`/blacklist-customers/${blacklistTenantId}`)
      .then(() => {
        dispatch("getBlacklistTenants");
        dispatch(
          "snackbar/successMessage",
          `Blocked tenant successfully deleted.`,
          { root: true }
        );
        commit("removeFromLoadingState", "deleting blacklist tenant");
      }).catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "deleting blacklist tenant");
        throw new Error(err);
      });
  }
};

export const mutations: MutationTree<BlacklistState> = {
  addToLoadingState: (state: BlacklistState, message: string) => {
    state.loadingState.push(message);
  },
  removeFromLoadingState: (state: BlacklistState, message: string) => {
    state.loadingState = state.loadingState.filter(loadingMessage => loadingMessage !== message);
  },
  setBlacklistReasons: (state: BlacklistState, reasons: BlacklistReasons[]) => {
    state.blacklistReasons = reasons;
  },
  setBlacklistTenants: (state: BlacklistState, tenants: BlacklistTenants[]) => {
    state.blacklistTenants = tenants;
  }
};

export default {
  name: "blacklist",
  namespaced: true,
  state,
  actions,
  mutations
};
